import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./context/AuthContext";

const C = { teal: "#00f5d4", red: "#ef476f", yellow: "#ffd166", green: "#7ae582" };

const S = {
  sectionLabel: { fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 2, marginBottom: 10, fontWeight: 700 },
  card: { background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "12px 14px", marginBottom: 8, border: "1px solid rgba(255,255,255,0.06)" },
  input: { width: "100%", padding: "9px 10px", borderRadius: 6, fontSize: 11, background: "rgba(0,0,0,0.3)", color: "#e0e6ed", border: "1px solid rgba(255,255,255,0.1)", outline: "none", fontFamily: "inherit", boxSizing: "border-box" },
  btnBase: { cursor: "pointer", fontFamily: "inherit", fontWeight: 700, letterSpacing: 1, borderRadius: 6 },
};

const ALERT_COLOR = { profit: C.teal, loss: C.red, buy: C.green, warn: C.yellow };
const alertColor  = (type) => ALERT_COLOR[type] || "rgba(255,255,255,0.1)";

function Sparkline({ data, width = 120, height = 36, color }) {
  if (!data || data.length < 2) return null;
  const prices = data.map(d => d.price);
  const min    = Math.min(...prices);
  const range  = Math.max(...prices) - min || 1;
  const points = prices.map((p, i) => `${(i / (prices.length - 1)) * width},${height - ((p - min) / range) * (height - 4) - 2}`).join(" ");
  const gradId = `sg-${color?.replace("#", "")}`;
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
      <polygon fill={`url(#${gradId})`} points={`0,${height} ${points} ${width},${height}`} />
    </svg>
  );
}

function MomentumGauge({ score, size = 60 }) {
  const angle  = Math.max(-90, Math.min(90, score * 15));
  const color  = score > 2 ? C.teal : score > 0 ? C.green : score > -2 ? C.yellow : C.red;
  const needle = { x: 50 + 30 * Math.cos((angle - 90) * Math.PI / 180), y: 50 + 30 * Math.sin((angle - 90) * Math.PI / 180) };
  return (
    <svg width={size} height={size / 2 + 8} viewBox="0 0 100 58" style={{ display: "block" }}>
      <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" strokeLinecap="round" />
      <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray="126" strokeDashoffset={126 - (angle + 90) / 180 * 126}
        style={{ transition: "stroke-dashoffset 0.8s ease, stroke 0.5s" }} />
      <line x1="50" y1="50" x2={needle.x} y2={needle.y}
        stroke={color} strokeWidth="2" strokeLinecap="round" style={{ transition: "all 0.8s ease" }} />
      <circle cx="50" cy="50" r="3" fill={color} />
    </svg>
  );
}

const TABS     = ["dashboard", "scanner", "positions", "history", "settings"];
const TAB_ICONS = { dashboard: "◉", scanner: "◎", positions: "▤", history: "↻", settings: "⚙" };

const DEFAULT_SETTINGS = {
  riskPerTrade: 2, maxConcurrent: 10, maxOpenTrades: 10, dailyMaxLoss: 6, maxDrawdown: 25,
  scanInterval: 60, scanUnit: "min", autoTrade: false, momentumThreshold: 2.0,
  trailingDropPct: 1.0, minimumNetProfitPercent: 2.5, minTrendProfitPercent: 2.5,
  topCoinsOnly: true, maxCoinsToTrade: 30, minVolumeUsd24h: 1000000, minPriceUsd: 0.25,
  maxSpreadPercent: 1.0, allowedQuoteCurrencies: ["USD", "USDC"],
  emergencyStopLossPct: 3.0, liveTradingEnabled: false,
  _version: 5,
};
function loadSettings() {
  const saved = loadFromStorage("mt_settings", null);
  if (!saved) return DEFAULT_SETTINGS;
  const merged = { ...DEFAULT_SETTINGS, ...saved };
  if (!saved._version || saved._version < 2) {
    merged.momentumThreshold = 0.5;
    merged._version = 2;
  }
  if (!saved._version || saved._version < 3) {
    merged.trailingDropPct     = merged.trailingDropPct     ?? 0.5;
    merged.emergencyStopLossPct = merged.emergencyStopLossPct ?? 3.0;
    merged._version = 3;
  }
  if (!saved._version || saved._version < 4) {
    merged.momentumThreshold = merged.momentumThreshold ?? 2.0;
    merged.trailingDropPct = merged.trailingDropPct ?? 1.0;
    merged.minimumNetProfitPercent = merged.minimumNetProfitPercent ?? 2.5;
    merged.minTrendProfitPercent = merged.minTrendProfitPercent ?? 2.5;
    merged._version = 4;
  }
  if (!saved._version || saved._version < 5) {
    merged.topCoinsOnly = merged.topCoinsOnly ?? true;
    merged.maxCoinsToTrade = merged.maxCoinsToTrade ?? 30;
    merged.minVolumeUsd24h = merged.minVolumeUsd24h ?? 1000000;
    merged.minPriceUsd = merged.minPriceUsd ?? 0.25;
    merged.maxSpreadPercent = merged.maxSpreadPercent ?? 1.0;
    merged.allowedQuoteCurrencies = merged.allowedQuoteCurrencies ?? ["USD", "USDC"];
    merged._version = 5;
  }
  return merged;
}
const DEFAULT_API_KEYS = { exchange: "coinbase", coinbaseApiKey: "", coinbaseApiSecret: "", binanceApiKey: "", binanceApiSecret: "", cdpApiKeyId: "", cdpApiKeySecret: "", cdpWalletSecret: "" };

function loadFromStorage(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}

export default function AltcoinMomentumTrader() {
  const { signOut } = useAuth();

  const [tab, setTab]               = useState("dashboard");
  const [balance, setBalance]       = useState(0);
  const [positions, setPositions]   = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [scanResults, setScanResults]   = useState([]);
  const [lastScan, setLastScan]     = useState(null);
  const [countdown, setCountdown]   = useState(60);
  const [dailyPnl, setDailyPnl]     = useState(0);
  const [totalPnl, setTotalPnl]     = useState(0);
  const [settings, setSettings]     = useState(() => loadSettings());
  const [apiKeys, setApiKeys]       = useState(() => {
    const stored = loadFromStorage("mt_apikeys", DEFAULT_API_KEYS);
    return stored.exchange === 'paper' ? { ...stored, exchange: 'coinbase' } : stored;
  });
  const [apiKeysSaved, setApiKeysSaved] = useState(false);
  const [testResult, setTestResult]   = useState(null); // { ok, msg }
  const [scanning, setScanning]     = useState(false);
  const [alerts, setAlerts]         = useState([]);
  const [connected, setConnected]   = useState(false);
  const [coinbaseAuthFailed, setCoinbaseAuthFailed] = useState(false);
  const [cashBalance, setCashBalance]               = useState(null); // null = unknown (live syncing)
  const [cryptoHoldings, setCryptoHoldings]         = useState([]);   // actual Coinbase holdings from sync
  const [pnlMismatch, setPnlMismatch]               = useState(null); // { drift, actualBalance, expectedBalance }
  const [balanceSynced, setBalanceSynced]           = useState(true); // false = live mode waiting for Coinbase sync

  const wsRef = useRef(null);

  // Persist settings/apiKeys to localStorage for instant display on next load
  useEffect(() => { localStorage.setItem("mt_settings", JSON.stringify(settings)); }, [settings]);
  useEffect(() => { localStorage.setItem("mt_apikeys",  JSON.stringify(apiKeys)); },  [apiKeys]);

  const addAlert = useCallback((msg, type = "info") => {
    setAlerts(prev => [{ msg, type, time: new Date(), id: Date.now() }, ...prev].slice(0, 50));
  }, []);

  // ─── WebSocket connection ──────────────────────────────────────────────────
  useEffect(() => {
    let ws;
    let dead          = false;
    let failCount     = 0;
    let alertTimer    = null;

    function connect() {
      if (dead) return;
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const url   = process.env.REACT_APP_WS_URL || `${proto}//${window.location.host}/ws`;
      ws          = new window.WebSocket(url);
      wsRef.current = ws;

      console.log('[ws] connecting to:', url);

      ws.onopen = () => {
        console.log('[ws] connected');
        failCount = 0;
        clearTimeout(alertTimer);
        setConnected(true);
      };

      ws.onmessage = e => {
        const msg = JSON.parse(e.data);

        if (msg.type === 'state') {
          setBalance(msg.balance);
          if (msg.cashBalance !== undefined) setCashBalance(msg.cashBalance);
          setDailyPnl(msg.dailyPnl);
          setTotalPnl(msg.totalPnl);
          setPositions(msg.positions || []);
          setTradeHistory((msg.trades || []).map(t => ({ ...t, id: t.id || Date.now(), entryTime: new Date(t.entryTime), exitTime: new Date(t.exitTime) })));
          setSettings(s => ({ ...s, ...(msg.settings || {}) }));
          setApiKeys(k => ({ ...k, exchange: msg.exchange || k.exchange }));
          if (msg.scanResults?.length) { setScanResults(msg.scanResults); setLastScan(new Date()); }
          setCountdown(msg.countdown || 60);
          setCoinbaseAuthFailed(msg.coinbaseAuthFailed || false);
          setBalanceSynced(msg.balanceSynced !== false);
        }

        if (msg.type === 'tick') {
          setCountdown(msg.countdown);
          if (msg.positions) setPositions(msg.positions);
          if (msg.scanResults?.length) setScanResults(msg.scanResults);
        }

        if (msg.type === 'scan_complete') {
          if (msg.scanResults?.length) { setScanResults(msg.scanResults); setLastScan(new Date()); }
          setCountdown(msg.countdown);
          if (msg.positions)  setPositions(msg.positions);
          if (msg.balance !== undefined) setBalance(msg.balance);
          setScanning(false);
        }

        if (msg.type === 'position_update') {
          if (msg.positions)                setPositions(msg.positions);
          if (msg.balance !== undefined)    setBalance(msg.balance);
          if (msg.cashBalance !== undefined) setCashBalance(msg.cashBalance);
          if (msg.dailyPnl !== undefined)   setDailyPnl(msg.dailyPnl);
          if (msg.totalPnl !== undefined)   setTotalPnl(msg.totalPnl);
          if (msg.cryptoHoldings)           setCryptoHoldings(msg.cryptoHoldings);
          setBalanceSynced(true);
        }

        if (msg.type === 'coinbase_auth_failed') { setCoinbaseAuthFailed(true); setBalanceSynced(true); }
        if (msg.type === 'coinbase_auth_ok')     setCoinbaseAuthFailed(false);
        if (msg.type === 'pnl_mismatch')         setPnlMismatch(msg);
        if (msg.type === 'alert')        addAlert(msg.msg, msg.alertType);
        if (msg.type === 'settings_ack') {
          setSettings(s => ({ ...s, ...(msg.settings || {}) }));
          setApiKeys(k => ({ ...k, exchange: msg.exchange || k.exchange }));
        }
        if (msg.type === 'connection_test') {
          if (msg.ok) {
            const cash   = Number(msg.cashBalance ?? msg.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const crypto = Number(msg.cryptoValue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const equity = Number(msg.totalEquity ?? msg.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            setTestResult({ ok: true, msg: `✅ Connected — Cash: $${cash} | Crypto: $${crypto} | Equity: $${equity} · ${msg.productCount ?? '?'} coins` });
            setBalance(msg.cashBalance ?? msg.balance);
            setCashBalance(msg.cashBalance ?? msg.balance);
            if (msg.cryptoHoldings?.length) setCryptoHoldings(msg.cryptoHoldings);
            setCoinbaseAuthFailed(false);
          } else {
            setTestResult({ ok: false, msg: `❌ Failed: ${msg.error}` });
          }
          setTimeout(() => setTestResult(null), 8000);
        }
      };

      // onerror is always followed by onclose — don't alert here, let onclose handle it
      ws.onerror = (err) => console.error('[ws] error:', err.message || err);

      ws.onclose = (e) => {
        console.log(`[ws] closed code=${e.code} reason=${e.reason}`);
        setConnected(false);
        wsRef.current = null;
        if (dead) return;

        failCount++;
        // Only show the user-facing alert after sustained disconnection (3+ attempts),
        // to avoid spamming on brief blips.
        if (failCount === 3) {
          addAlert('⚠️ Cannot reach server — trading continues in background', 'warn');
        }

        // code 1008 = auth rejection — back off longer, no point hammering
        const base  = e.code === 1008 ? 15_000 : 3_000;
        const delay = Math.min(base * Math.min(failCount, 4), 30_000);
        setTimeout(connect, delay);
      };
    }

    connect();
    return () => { dead = true; clearTimeout(alertTimer); ws?.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Send helpers ──────────────────────────────────────────────────────────
  function wsSend(msg) {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(msg));
  }

  const [buyModal, setBuyModal] = useState(null); // { symbol, price }
  const [buyAmount, setBuyAmount] = useState('');

  const executeBuy = (symbol, price) => {
    setBuyAmount('');
    setBuyModal({ symbol, price });
  };

  const confirmBuy = () => {
    const amount = parseFloat(buyAmount);
    if (!amount || amount <= 0) return;
    wsSend({ type: 'buy', symbol: buyModal.symbol, amount });
    setBuyModal(null);
  };

  const executeSell = (symbol) => wsSend({ type: 'sell', symbol });

  const handleScanNow = useCallback(() => {
    setScanning(true);
    wsSend({ type: 'scan_now' });
    setTimeout(() => setScanning(false), 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push settings to server whenever they change
  const pushSettings = useCallback((newSettings, newApiKeys) => {
    wsSend({ type: 'settings', settings: newSettings, exchange: newApiKeys?.exchange, apiKeys: newApiKeys });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSettingsChange = (key, value) => {
    setSettings(s => {
      const updated = { ...s, [key]: value };
      pushSettings(updated, apiKeys);
      return updated;
    });
  };

  const handleApiKeysChange = (updates) => {
    setApiKeys(k => {
      const updated = { ...k, ...updates };
      pushSettings(settings, updated);
      return updated;
    });
  };

  const formatPrice = (p) => {
    if (!p && p !== 0) return "—";
    if (p < 0.001) return p.toFixed(7);
    if (p < 1)     return p.toFixed(4);
    return p.toFixed(2);
  };

  const formatCountdown = (secs) => {
    if (secs >= 86400) return `${Math.floor(secs / 86400)}d ${String(Math.floor((secs % 86400) / 3600)).padStart(2,'0')}h`;
    if (secs >= 3600)  return `${Math.floor(secs / 3600)}h ${String(Math.floor((secs % 3600) / 60)).padStart(2,'0')}m`;
    return `${String(Math.floor(secs / 60)).padStart(2,'0')}:${String(secs % 60).padStart(2,'0')}`;
  };

  const positionValue  = positions.reduce((s, p) => s + (p.currentPrice || p.entry) * (p.quantity || 0), 0);
  const isLiveMode     = apiKeys.exchange === 'coinbase' || apiKeys.exchange === 'cdp_evm';
  const cryptoUsdValue = cryptoHoldings.reduce((s, h) => s + (h.usdValue || 0), 0);
  const displayCrypto  = isLiveMode ? (cryptoHoldings.length > 0 ? cryptoUsdValue : positionValue) : null;
  const totalEquity    = isLiveMode && cryptoHoldings.length > 0 ? balance + cryptoUsdValue : balance + positionValue;
  const startingCapital = totalEquity - totalPnl;
  const accountReturn   = startingCapital > 0 ? (totalPnl / startingCapital) * 100 : 0;
  const dailyPnlPct    = totalEquity > 0 ? (dailyPnl / totalEquity) * 100 : 0;
  const displayCash         = isLiveMode && cashBalance !== null ? cashBalance : balance;
  const authBad             = coinbaseAuthFailed && (apiKeys.exchange === 'coinbase' || apiKeys.exchange === 'cdp_evm');
  const isBalanceSyncing    = isLiveMode && !balanceSynced && !authBad;
  const isSurvivalMode  = !authBad && (-accountReturn > settings.maxDrawdown);
  const isDailyLimitHit = Math.abs(dailyPnlPct) > settings.dailyMaxLoss && dailyPnl < 0;
  const scanSecs        = Number(settings.scanInterval) || 3600;
  const countdownColor  = countdown < scanSecs * 0.1 ? C.red : countdown < scanSecs * 0.25 ? C.yellow : C.teal;

  return (
    <div style={{ fontFamily: "'JetBrains Mono', 'SF Mono', 'Fira Code', monospace", background: "#0a0e17", color: "#e0e6ed", minHeight: "100vh", maxWidth: 480, margin: "0 auto", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "fixed", top: -200, right: -200, width: 500, height: 500, background: `radial-gradient(circle, rgba(0,245,212,0.06) 0%, transparent 70%)`, pointerEvents: "none", zIndex: 0 }} />

      {/* Header */}
      <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid rgba(255,255,255,0.06)", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/Logo.png" alt="OLMEM" style={{ height: 48, width: "auto" }} />
            <div>
              <div style={{ fontSize: 11, color: C.teal, letterSpacing: 3, fontWeight: 700 }}>MOMENTUM</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", marginTop: 2 }}>TRADER</div>
            </div>
          </div>
          <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
            <div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", letterSpacing: 1 }}>NEXT SCAN</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: countdownColor, fontVariantNumeric: "tabular-nums" }}>{formatCountdown(countdown)}</div>
            </div>
            <button
              onClick={signOut}
              style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.3)", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", fontFamily: "inherit", letterSpacing: 1, fontWeight: 700 }}
            >SIGN OUT</button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          {isSurvivalMode   && <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: "rgba(239,71,111,0.2)", color: C.red, border: "1px solid rgba(239,71,111,0.3)", letterSpacing: 1, fontWeight: 700 }}>⚠ SURVIVAL MODE</span>}
          {isDailyLimitHit  && <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: "rgba(255,209,102,0.2)", color: C.yellow, border: "1px solid rgba(255,209,102,0.3)", letterSpacing: 1, fontWeight: 700 }}>DAILY LIMIT HIT</span>}
          <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: settings.autoTrade ? "rgba(0,245,212,0.15)" : "rgba(255,255,255,0.05)", color: settings.autoTrade ? C.teal : "rgba(255,255,255,0.4)", border: `1px solid ${settings.autoTrade ? "rgba(0,245,212,0.3)" : "rgba(255,255,255,0.08)"}`, letterSpacing: 1, fontWeight: 700 }}>{settings.autoTrade ? "AUTO" : "MANUAL"}</span>
          <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: connected ? "rgba(0,245,212,0.08)" : "rgba(255,255,255,0.04)", color: connected ? C.teal : "rgba(255,255,255,0.25)", border: `1px solid ${connected ? "rgba(0,245,212,0.2)" : "rgba(255,255,255,0.06)"}`, letterSpacing: 1 }}>{connected ? "● LIVE" : "○ CONNECTING"}</span>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 1, background: "rgba(255,255,255,0.03)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {[
          { label: "EQUITY",   value: authBad ? "Auth Failed" : isBalanceSyncing ? "Syncing..." : `$${totalEquity.toFixed(0)}`, color: authBad ? C.red : isBalanceSyncing ? C.yellow : "#fff" },
          { label: "TODAY P&L", value: `${dailyPnl >= 0 ? "+" : ""}$${dailyPnl.toFixed(0)}`, color: dailyPnl >= 0 ? C.teal : C.red },
          { label: "RETURN",   value: `${accountReturn >= 0 ? "+" : ""}${accountReturn.toFixed(1)}%`, color: accountReturn >= 0 ? C.green : accountReturn > -5 ? C.yellow : C.red },
          { label: "CASH",     value: isBalanceSyncing ? "Syncing..." : `$${displayCash.toFixed(0)}`, color: isBalanceSyncing ? C.yellow : C.teal },
          { label: "CRYPTO",   value: `$${(displayCrypto ?? 0).toFixed(0)}`, color: C.green },
        ].map((item, i) => (
          <div key={i} style={{ padding: "10px 6px", textAlign: "center" }}>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.35)", letterSpacing: 1.5, marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: item.color, fontVariantNumeric: "tabular-nums" }}>{item.value}</div>
          </div>
        ))}
      </div>
      {isLiveMode && !authBad && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: "rgba(255,255,255,0.02)", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          <div style={{ padding: "6px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, marginBottom: 2 }}>CASH TO TRADE</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: isBalanceSyncing ? C.yellow : C.teal, fontVariantNumeric: "tabular-nums" }}>{isBalanceSyncing ? "Syncing Coinbase balance…" : `$${displayCash.toFixed(2)}`}</div>
          </div>
          <div style={{ padding: "6px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, marginBottom: 2 }}>CRYPTO VALUE</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)", fontVariantNumeric: "tabular-nums" }}>${(displayCrypto ?? 0).toFixed(2)}</div>
          </div>
        </div>
      )}
      {cryptoHoldings.length > 0 && (
        <div style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", padding: "8px 16px" }}>
          <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: 1.5, marginBottom: 6 }}>COIN HOLDINGS</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {cryptoHoldings.map(h => (
              <div key={h.symbol} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>{h.symbol}</span>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", fontVariantNumeric: "tabular-nums" }}>{h.total.toFixed(6).replace(/\.?0+$/, '')}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.teal, fontVariantNumeric: "tabular-nums" }}>${(h.usdValue || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {pnlMismatch && (
        <div style={{ padding: "8px 16px", background: "rgba(239,71,111,0.15)", borderBottom: "1px solid rgba(239,71,111,0.3)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 10, color: C.red, fontWeight: 700 }}>
            ⚠️ P&L MISMATCH — Auto-trading paused. Expected ${(pnlMismatch.expectedBalance ?? 0).toFixed(2)}, Coinbase: ${(pnlMismatch.actualBalance ?? 0).toFixed(2)}
          </div>
          <button onClick={() => setPnlMismatch(null)} style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: "rgba(239,71,111,0.2)", color: C.red, border: "1px solid rgba(239,71,111,0.4)", cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>✕</button>
        </div>
      )}

      <div style={{ paddingBottom: 72, position: "relative", zIndex: 1 }}>

        {/* DASHBOARD */}
        {tab === "dashboard" && (
          <div style={{ padding: 16 }}>
            <button onClick={handleScanNow} disabled={scanning} style={{ width: "100%", marginBottom: 16, padding: 12, background: scanning ? "rgba(0,245,212,0.25)" : "linear-gradient(135deg, rgba(0,245,212,0.18), rgba(122,229,130,0.12))", color: C.teal, border: "1px solid rgba(0,245,212,0.35)", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: scanning ? "default" : "pointer", letterSpacing: 2, fontFamily: "inherit", transition: "background 0.3s" }}>
              {scanning ? "⟳ SCANNING MARKETS…" : "⟳ SCAN MARKETS NOW"}
            </button>

            {(isLiveMode || cryptoHoldings.length > 0) && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
                <div style={{ padding: "12px 16px", background: "rgba(0,245,212,0.06)", border: "1px solid rgba(0,245,212,0.15)", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, marginBottom: 4 }}>CASH</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.teal, fontVariantNumeric: "tabular-nums" }}>${displayCash.toFixed(2)}</div>
                </div>
                <div style={{ padding: "12px 16px", background: "rgba(122,229,130,0.06)", border: "1px solid rgba(122,229,130,0.15)", borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", letterSpacing: 1.5, marginBottom: 4 }}>CRYPTO</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: C.green, fontVariantNumeric: "tabular-nums" }}>${(displayCrypto ?? 0).toFixed(2)}</div>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <div style={{ ...S.sectionLabel }}>ACTIVE POSITIONS ({positions.length}/{settings.maxOpenTrades ?? settings.maxConcurrent})</div>
              {positions.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 8, color: "rgba(255,255,255,0.25)", fontSize: 12 }}>
                  No active positions — Scanner will identify entries
                </div>
              ) : positions.map(pos => (
                <div key={pos.id || pos.symbol} style={{ ...S.card, border: `1px solid ${pos.pnl >= 0 ? "rgba(0,245,212,0.15)" : "rgba(239,71,111,0.15)"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{pos.symbol}</span>
                      <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", marginLeft: 8 }}>{pos.entry && `@ ${formatPrice(pos.entry)}`}</span>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: pos.pnl >= 0 ? C.teal : C.red }}>{pos.pnl >= 0 ? "+" : ""}{pos.pnl?.toFixed(2)}%</div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", display: "flex", gap: 12 }}>
                      <span>SL: {formatPrice(pos.stopLoss)}</span>
                      <span>High: ${formatPrice(pos.highPrice)}</span>
                    </div>
                    <button onClick={() => executeSell(pos.symbol)} style={{ background: "rgba(239,71,111,0.15)", color: C.red, border: "1px solid rgba(239,71,111,0.3)", borderRadius: 4, padding: "4px 12px", fontSize: 10, fontWeight: 700, cursor: "pointer", letterSpacing: 1, fontFamily: "inherit" }}>SELL</button>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 10, color: "rgba(255,255,255,0.35)", display: "flex", gap: 12 }}>
                    {pos.quantity != null && <span>Qty: {pos.quantity < 0.001 ? pos.quantity.toFixed(6) : pos.quantity.toFixed(4)} {pos.symbol?.replace("-USD", "").replace("USD", "")}</span>}
                    {pos.cost != null && <span>Cost: ${pos.cost.toFixed(2)}</span>}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <div style={{ ...S.sectionLabel }}>ACTIVITY LOG</div>
              <div style={{ maxHeight: 240, overflowY: "auto" }}>
                {alerts.slice(0, 15).map(alert => (
                  <div key={alert.id} style={{ padding: "8px 12px", marginBottom: 4, borderRadius: 6, fontSize: 11, background: `${alertColor(alert.type)}1a`, borderLeft: `2px solid ${alertColor(alert.type)}`, color: "rgba(255,255,255,0.7)" }}>
                    <div>{alert.msg}</div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.25)", marginTop: 3 }}>{alert.time.toLocaleTimeString()}</div>
                  </div>
                ))}
                {alerts.length === 0 && <div style={{ color: "rgba(255,255,255,0.2)", fontSize: 11, textAlign: "center", padding: 16 }}>No activity yet</div>}
              </div>
            </div>
          </div>
        )}

        {/* SCANNER */}
        {tab === "scanner" && (
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ ...S.sectionLabel, marginBottom: 0 }}>MOMENTUM SCANNER</div>
              <button onClick={handleScanNow} disabled={scanning} style={{ background: scanning ? "rgba(0,245,212,0.25)" : "rgba(0,245,212,0.12)", color: C.teal, border: "1px solid rgba(0,245,212,0.25)", borderRadius: 4, padding: "4px 12px", fontSize: 10, fontWeight: 700, cursor: scanning ? "default" : "pointer", letterSpacing: 1, fontFamily: "inherit", transition: "background 0.3s" }}>{scanning ? "SCANNING…" : "SCAN NOW"}</button>
            </div>
            {lastScan && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", marginBottom: 12 }}>Last scan: {lastScan.toLocaleTimeString()}</div>}

            {scanResults.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 8, color: "rgba(255,255,255,0.3)", fontSize: 12 }}>
                No high-liquidity top-30 coins currently meet trade criteria.
              </div>
            )}

            {scanResults.map((coin, idx) => {
              const m         = coin.momentum;
              const decision  = coin.decision || { status: "blocked", allowed: false, reason: "decision unavailable", reasons: [] };
              const isHeld    = positions.find(p => p.symbol === coin.symbol);
              const trendColor = m.trend === "bullish" ? C.teal : m.trend === "bearish" ? C.red : C.yellow;
              const decisionColor = decision.status === "pass"
                ? C.green
                : decision.status === "held"
                  ? C.yellow
                  : C.red;
              const decisionLabel = decision.status === "pass"
                ? "PASS"
                : decision.status === "held"
                  ? "HELD"
                  : "BLOCKED";
              return (
                <div key={coin.symbol} style={{ background: idx < 3 ? "rgba(0,245,212,0.04)" : "rgba(255,255,255,0.02)", border: `1px solid ${idx < 3 ? "rgba(0,245,212,0.1)" : "rgba(255,255,255,0.04)"}`, borderRadius: 8, padding: 12, marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        {idx < 3 && <span style={{ fontSize: 8, background: "rgba(0,245,212,0.2)", color: C.teal, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>#{idx + 1}</span>}
                        <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{coin.symbol}</span>
                        <span style={{ fontSize: 10, color: "rgba(255,255,255,0.3)" }}>{coin.name}</span>
                        {isHeld && <span style={{ fontSize: 8, background: "rgba(122,229,130,0.2)", color: C.green, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>HELD</span>}
                        <span style={{ fontSize: 8, background: `${decisionColor}22`, color: decisionColor, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>{decisionLabel}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#fff", marginBottom: 4 }}>${formatPrice(coin.currentPrice)}</div>
                      <div style={{ display: "flex", gap: 10, fontSize: 10, color: "rgba(255,255,255,0.4)" }}>
                        <span style={{ color: trendColor }}>{m.trend === "bullish" ? "▲" : m.trend === "bearish" ? "▼" : "━"} {m.pctChange}%</span>
                        <span>Vol: {m.volRatio}x</span>
                        <span>Score: {m.score}</span>
                      </div>
                      <div style={{ marginTop: 8, fontSize: 10, color: decision.allowed ? "rgba(122,229,130,0.85)" : "rgba(255,255,255,0.55)" }}>
                        {decision.allowed
                          ? `Entry passed${decision.expectedMovePct != null && decision.requiredMovePct != null ? ` · Move ${decision.expectedMovePct.toFixed(2)}% vs req ${decision.requiredMovePct.toFixed(2)}%` : ""}`
                          : (decision.reason || "Entry blocked")}
                      </div>
                      {!decision.allowed && decision.reasons?.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                          {decision.reasons.slice(0, 3).map(reason => (
                            <span key={reason} style={{ fontSize: 8, padding: "2px 6px", borderRadius: 999, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.08)" }}>
                              {reason.replace(/^skip buy:\s*/i, "")}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                      <MomentumGauge score={m.score} size={50} />
                      <Sparkline data={coin.history?.slice(-20)} width={90} height={28} color={trendColor} />
                    </div>
                  </div>
                  {!isHeld && (
                    <button onClick={() => executeBuy(coin.symbol, coin.history?.at(-1)?.price)} style={{ width: "100%", marginTop: 8, padding: 8, background: decision.allowed ? "linear-gradient(135deg, rgba(0,245,212,0.2), rgba(122,229,130,0.15))" : "rgba(255,255,255,0.04)", color: decision.allowed ? C.teal : "rgba(255,255,255,0.35)", border: `1px solid ${decision.allowed ? "rgba(0,245,212,0.3)" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 1.5, fontFamily: "inherit" }}>
                      {decision.allowed ? `BUY ${coin.symbol}` : `OVERRIDE BUY ${coin.symbol}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* POSITIONS */}
        {tab === "positions" && (
          <div style={{ padding: 16 }}>
            <div style={{ ...S.sectionLabel }}>OPEN POSITIONS</div>
            {positions.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 8 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>◇</div>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>No open positions</div>
              </div>
            ) : positions.map(pos => {
              const profitDollar = pos.unrealizedPnL ?? ((pos.currentPrice || pos.entry) - pos.entry) * pos.quantity;
              const isLivePos = pos.live && isLiveMode;
              return (
                <div key={pos.id || pos.symbol} style={{ ...S.card, borderRadius: 10, padding: 16, marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{pos.symbol}</div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 2 }}>Opened {new Date(pos.entryTime).toLocaleTimeString()}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: (pos.pnl || 0) >= 0 ? C.teal : C.red }}>{(pos.pnl || 0) >= 0 ? "+" : ""}{(pos.pnl || 0).toFixed(2)}%</div>
                      <div style={{ fontSize: 12, color: profitDollar >= 0 ? C.teal : C.red }}>
                        {profitDollar >= 0 ? "+" : ""}${profitDollar.toFixed(2)}{isLivePos ? <span style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginLeft: 3 }}>est.</span> : null}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                    {[{ label: "ENTRY", value: formatPrice(pos.entry) }, { label: "CURRENT", value: formatPrice(pos.currentPrice || pos.entry) }, { label: "QTY", value: pos.quantity?.toFixed(4) }].map((d, i) => (
                      <div key={i} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 6, padding: "8px 10px" }}>
                        <div style={{ fontSize: 8, color: "rgba(255,255,255,0.3)", letterSpacing: 1, marginBottom: 3 }}>{d.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", fontVariantNumeric: "tabular-nums" }}>{d.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 8, fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 12 }}>
                    <span>SL: ${formatPrice(pos.stopLoss)}</span><span>·</span>
                    <span>High: ${formatPrice(pos.highPrice)}</span><span>·</span>
                    <span>Cost: ${pos.cost?.toFixed(2)}</span>
                  </div>
                  <button onClick={() => executeSell(pos.symbol)} style={{ width: "100%", padding: 10, background: "rgba(239,71,111,0.12)", color: C.red, border: "1px solid rgba(239,71,111,0.25)", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 1, fontFamily: "inherit" }}>SELL NOW</button>
                </div>
              );
            })}
          </div>
        )}

        {/* HISTORY */}
        {tab === "history" && (
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ ...S.sectionLabel, marginBottom: 0 }}>TRADE HISTORY</div>
              <div style={{ fontSize: 11, color: totalPnl >= 0 ? C.teal : C.red, fontWeight: 700 }}>Total: {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}</div>
            </div>
            {tradeHistory.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", border: "1px dashed rgba(255,255,255,0.08)", borderRadius: 8 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>↻</div>
                <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 12 }}>No trades yet</div>
              </div>
            ) : tradeHistory.map(trade => {
              const grossProfit = (trade.exit - trade.entry) * (trade.quantity || 0);
              const fees        = grossProfit - (trade.profit || 0);
              const isStopLoss  = trade.reason?.includes('Stop') || trade.reason?.includes('stop');
              const outcomeColor = trade.profit >= 0 ? C.teal : C.red;
              return (
                <div key={trade.id} style={{ background: "rgba(255,255,255,0.03)", borderLeft: `3px solid ${outcomeColor}`, borderRadius: "0 8px 8px 0", padding: 12, marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{trade.symbol}</span>
                      <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.4)" }}>{trade.reason}</span>
                      {isStopLoss && trade.profit < 0 && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: "rgba(239,71,111,0.15)", color: C.red, fontWeight: 700 }}>STOP-LOSS EXIT</span>}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: outcomeColor }}>{trade.pnl >= 0 ? "+" : ""}{trade.pnl?.toFixed(2)}%</div>
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 10, color: "rgba(255,255,255,0.3)", marginTop: 6, flexWrap: "wrap" }}>
                    <span>In: ${formatPrice(trade.entry)}</span>
                    <span>Out: ${formatPrice(trade.exit)}</span>
                    <span style={{ color: outcomeColor, fontWeight: 700 }}>Net P&L: {trade.profit >= 0 ? "+" : ""}${(trade.profit ?? 0).toFixed(2)}</span>
                    {fees > 0.001 && <span style={{ color: "rgba(255,209,102,0.7)" }}>Fees: ${fees.toFixed(2)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && (
          <div style={{ padding: 16 }}>
            <div style={{ ...S.sectionLabel }}>CONNECTION</div>

            <div style={{ ...S.card }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Exchange / Mode</div>
              <div style={{ display: "flex", gap: 6 }}>
                {[{ value: "paper", label: "Paper Trade" }, { value: "coinbase", label: "Coinbase" }, { value: "cdp_evm", label: "CDP Base" }, { value: "binance", label: "Binance" }].map(opt => (
                  <button key={opt.value} onClick={() => handleApiKeysChange({ exchange: opt.value })} style={{ flex: 1, padding: "7px 4px", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5, fontFamily: "inherit", background: apiKeys.exchange === opt.value ? "rgba(0,245,212,0.2)" : "rgba(255,255,255,0.04)", color: apiKeys.exchange === opt.value ? C.teal : "rgba(255,255,255,0.35)", border: `1px solid ${apiKeys.exchange === opt.value ? "rgba(0,245,212,0.4)" : "rgba(255,255,255,0.08)"}`, transition: "all 0.2s" }}>{opt.label}</button>
                ))}
              </div>
            </div>

            {apiKeys.exchange !== "paper" && (() => {
              const ex = apiKeys.exchange;
              if (ex === "cdp_evm") {
                return (
                  <div style={{ ...S.card, border: "1px solid rgba(0,245,212,0.1)" }}>
                    <div style={{ fontSize: 10, color: C.teal, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>CDP BASE (EVM SWAPS)</div>
                    {[
                      { key: "cdpApiKeyId",     label: "API Key ID",      placeholder: "Your CDP API key ID" },
                      { key: "cdpApiKeySecret", label: "API Key Secret",  placeholder: "Your CDP API key secret (private key)" },
                      { key: "cdpWalletSecret", label: "Wallet Secret",   placeholder: "Your CDP wallet secret" },
                    ].map(field => (
                      <div key={field.key} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 5 }}>{field.label}</div>
                        <input type="password" value={apiKeys[field.key] || ""} onChange={e => handleApiKeysChange({ [field.key]: e.target.value })} placeholder={field.placeholder} style={S.input} />
                      </div>
                    ))}
                    <div style={{ fontSize: 9, color: "rgba(0,245,212,0.45)", marginTop: 4 }}>Swaps USDC ↔ token on Base mainnet. Hold USDC + ETH (gas) in your CDP wallet.</div>
                  </div>
                );
              }
              const color  = ex === "coinbase" ? C.teal : C.yellow;
              const title  = ex === "coinbase" ? "COINBASE ADVANCED TRADE" : "BINANCE API";
              const fields = [{ key: `${ex}ApiKey`, label: "API Key" }, { key: `${ex}ApiSecret`, label: "API Secret" }];
              return (
                <div style={{ ...S.card, border: `1px solid ${color}1a` }}>
                  <div style={{ fontSize: 10, color, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>{title}</div>
                  {fields.map(field => (
                    <div key={field.key} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 5 }}>{field.label}</div>
                      {ex === "coinbase" && field.key === "coinbaseApiSecret"
                        ? <textarea value={apiKeys[field.key] || ""} onChange={e => handleApiKeysChange({ [field.key]: e.target.value })} placeholder={"Paste PEM private key\n-----BEGIN EC PRIVATE KEY-----\n..."} rows={4} style={{ ...S.input, height: "auto", resize: "vertical", fontFamily: "monospace", fontSize: 9, lineHeight: 1.4 }} />
                        : <input type="password" value={apiKeys[field.key] || ""} onChange={e => handleApiKeysChange({ [field.key]: e.target.value })} placeholder={`Paste your ${field.label.toLowerCase()}…`} style={S.input} />
                      }
                    </div>
                  ))}
                </div>
              );
            })()}

            {apiKeys.exchange !== "paper" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <button onClick={() => { pushSettings(settings, apiKeys); setApiKeysSaved(true); setTimeout(() => setApiKeysSaved(false), 2000); }} style={{ flex: 2, padding: 10, background: apiKeysSaved ? "rgba(122,229,130,0.2)" : "rgba(0,245,212,0.12)", color: apiKeysSaved ? C.green : C.teal, border: `1px solid ${apiKeysSaved ? "rgba(122,229,130,0.4)" : "rgba(0,245,212,0.25)"}`, borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 1.5, fontFamily: "inherit", transition: "all 0.3s" }}>
                  {apiKeysSaved ? "✓ KEYS SAVED" : "SAVE API KEYS"}
                </button>
                <button onClick={() => { setTestResult({ ok: null, msg: 'Testing…' }); wsSend({ type: "test_connection" }); }} style={{ flex: 1, padding: 10, background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 10, fontWeight: 700, cursor: "pointer", letterSpacing: 1, fontFamily: "inherit" }}>
                  TEST
                </button>
              </div>
            )}
            {testResult && (
              <div style={{ marginBottom: 12, padding: "8px 12px", borderRadius: 6, fontSize: 10, background: testResult.ok === true ? "rgba(0,245,212,0.08)" : testResult.ok === false ? "rgba(239,71,111,0.08)" : "rgba(255,255,255,0.04)", color: testResult.ok === true ? C.teal : testResult.ok === false ? C.red : "rgba(255,255,255,0.5)", border: `1px solid ${testResult.ok === true ? "rgba(0,245,212,0.2)" : testResult.ok === false ? "rgba(239,71,111,0.2)" : "rgba(255,255,255,0.08)"}` }}>
                {testResult.msg}
              </div>
            )}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, padding: "8px 12px", borderRadius: 6, background: connected ? "rgba(0,245,212,0.06)" : "rgba(255,255,255,0.03)", border: `1px solid ${connected ? "rgba(0,245,212,0.2)" : "rgba(255,255,255,0.06)"}` }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: connected ? C.teal : C.yellow, boxShadow: connected ? `0 0 6px ${C.teal}` : "none" }} />
              <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>
                {connected
                  ? apiKeys.exchange === "paper"
                    ? "Paper trading — server-side simulation running 24/7"
                    : `Live mode — ${apiKeys.exchange} — server-side 24/7`
                  : "Connecting to server…"}
              </span>
            </div>

            <div style={{ ...S.sectionLabel, marginTop: 4 }}>TRADING PARAMETERS</div>
            <div style={{ fontSize: 9, color: "rgba(0,245,212,0.5)", marginBottom: 10 }}>✓ Settings sync to server and persist across sessions</div>

            {[
              { key: "maxCoinsToTrade",     label: "Max Coins In Universe",       min: 5,   max: 30,   step: 1    },
              { key: "minVolumeUsd24h",    label: "Min 24h Volume (USD)",        min: 1000000, max: 10000000, step: 250000 },
              { key: "minPriceUsd",        label: "Min Price (USD)",             min: 0.25, max: 10.0, step: 0.25 },
              { key: "maxSpreadPercent",   label: "Max Spread (%)",              min: 0.25, max: 2.0, step: 0.05 },
              { key: "maxOpenTrades",       label: "Max Open Trades",             min: 1,   max: 20,  step: 1   },
              { key: "minimumNetProfitPercent", label: "Min Expected Net Profit %", min: 0.5, max: 8.0, step: 0.25 },
              { key: "minTrendProfitPercent",   label: "Arm Trail After Net %",     min: 0.5, max: 8.0, step: 0.25 },
              { key: "trailingDropPct",     label: "Trailing Sell Drop %",        min: 0.40, max: 5.00, step: 0.05 },
              { key: "dailyMaxLoss",        label: "Daily Max Loss (%)",          min: 2,   max: 20,  step: 1   },
              { key: "maxDrawdown",         label: "Max Account Loss (%)",        min: 5,   max: 50,  step: 1   },
              { key: "momentumThreshold",   label: "Momentum Buy Threshold",      min: 0.5, max: 5,   step: 0.25 },
            ].map(param => (
              <div key={param.key} style={{ ...S.card, border: "1px solid rgba(255,255,255,0.04)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>{param.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.teal, fontVariantNumeric: "tabular-nums" }}>
                    {param.key === "minVolumeUsd24h"
                      ? Number(settings[param.key] || 0).toLocaleString()
                      : settings[param.key]}
                  </span>
                </div>
                <input type="range" min={param.min} max={param.max} step={param.step} value={settings[param.key]} onChange={e => handleSettingsChange(param.key, parseFloat(e.target.value))} style={{ width: "100%", height: 4, appearance: "none", background: "rgba(255,255,255,0.1)", borderRadius: 2, outline: "none", cursor: "pointer", accentColor: C.teal }} />
              </div>
            ))}

            <div style={{ ...S.card, border: "1px solid rgba(255,255,255,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Top 30 Coins Only</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>Restrict scanning to large liquid Coinbase assets</div>
              </div>
              <div onClick={() => handleSettingsChange('topCoinsOnly', !settings.topCoinsOnly)} style={{ width: 44, height: 24, borderRadius: 12, cursor: "pointer", background: settings.topCoinsOnly ? "rgba(0,245,212,0.3)" : "rgba(255,255,255,0.1)", border: `1px solid ${settings.topCoinsOnly ? "rgba(0,245,212,0.5)" : "rgba(255,255,255,0.15)"}`, position: "relative", transition: "all 0.3s" }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: settings.topCoinsOnly ? C.teal : "rgba(255,255,255,0.3)", position: "absolute", top: 2, left: settings.topCoinsOnly ? 22 : 2, transition: "all 0.3s" }} />
              </div>
            </div>

            <div style={{ ...S.card, border: "1px solid rgba(255,255,255,0.04)", padding: 14 }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 8 }}>Allowed Quote Currencies</div>
              <div style={{ display: "flex", gap: 8 }}>
                {["USD", "USDC"].map(currency => {
                  const selected = (settings.allowedQuoteCurrencies || []).includes(currency);
                  return (
                    <button
                      key={currency}
                      onClick={() => {
                        const next = selected
                          ? (settings.allowedQuoteCurrencies || []).filter(v => v !== currency)
                          : [...(settings.allowedQuoteCurrencies || []), currency];
                        handleSettingsChange('allowedQuoteCurrencies', next.length ? next : [currency]);
                      }}
                      style={{ fontSize: 10, padding: "6px 10px", borderRadius: 6, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", background: selected ? "rgba(0,245,212,0.18)" : "rgba(255,255,255,0.05)", color: selected ? C.teal : "rgba(255,255,255,0.45)", border: `1px solid ${selected ? "rgba(0,245,212,0.35)" : "rgba(255,255,255,0.08)"}` }}
                    >
                      {currency}
                    </button>
                  );
                })}
              </div>
            </div>

            {(() => {
              const UNITS = {
                min: { mult: 60,    min: 1,  max: 59,  step: 1,  suffix: "m", label: "MIN" },
                hr:  { mult: 3600,  min: 1,  max: 23,  step: 1,  suffix: "h", label: "HR"  },
                day: { mult: 86400, min: 1,  max: 30,  step: 1,  suffix: "d", label: "DAY" },
              };
              const unit     = UNITS[settings.scanUnit] ? settings.scanUnit : "min";
              const cfg      = UNITS[unit];
              const sliderVal = Math.max(cfg.min, Math.min(cfg.max, Math.round(settings.scanInterval / cfg.mult)));
              return (
                <div style={{ ...S.card, border: "1px solid rgba(255,255,255,0.04)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Scan Interval</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {Object.keys(UNITS).map(u => (
                        <button key={u} onClick={() => {
                          const newCfg  = UNITS[u];
                          const clamped = Math.max(newCfg.min, Math.min(newCfg.max, Math.round(settings.scanInterval / newCfg.mult)));
                          handleSettingsChange('scanUnit', u);
                          handleSettingsChange('scanInterval', clamped * newCfg.mult);
                        }} style={{ fontSize: 9, padding: "2px 7px", borderRadius: 3, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", background: unit === u ? "rgba(0,245,212,0.2)" : "rgba(255,255,255,0.05)", color: unit === u ? C.teal : "rgba(255,255,255,0.3)", border: `1px solid ${unit === u ? "rgba(0,245,212,0.35)" : "rgba(255,255,255,0.08)"}` }}>{UNITS[u].label}</button>
                      ))}
                      <span style={{ fontSize: 14, fontWeight: 700, color: C.teal, fontVariantNumeric: "tabular-nums", minWidth: 36, textAlign: "right" }}>{sliderVal}{cfg.suffix}</span>
                    </div>
                  </div>
                  <input type="range" min={cfg.min} max={cfg.max} step={cfg.step} value={sliderVal}
                    onChange={e => handleSettingsChange('scanInterval', parseInt(e.target.value) * cfg.mult)}
                    style={{ width: "100%", height: 4, appearance: "none", background: "rgba(255,255,255,0.1)", borderRadius: 2, outline: "none", cursor: "pointer", accentColor: C.teal }} />
                </div>
              );
            })()}

            <div style={{ ...S.card, border: "1px solid rgba(255,255,255,0.04)", display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14 }}>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Auto-Trade Mode</div>
                <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>Server executes trades automatically — runs 24/7</div>
              </div>
              <div onClick={() => handleSettingsChange('autoTrade', !settings.autoTrade)} style={{ width: 44, height: 24, borderRadius: 12, cursor: "pointer", background: settings.autoTrade ? "rgba(0,245,212,0.3)" : "rgba(255,255,255,0.1)", border: `1px solid ${settings.autoTrade ? "rgba(0,245,212,0.5)" : "rgba(255,255,255,0.15)"}`, position: "relative", transition: "all 0.3s" }}>
                <div style={{ width: 18, height: 18, borderRadius: "50%", background: settings.autoTrade ? C.teal : "rgba(255,255,255,0.3)", position: "absolute", top: 2, left: settings.autoTrade ? 22 : 2, transition: "all 0.3s" }} />
              </div>
            </div>

            {isLiveMode && (
              <div style={{ ...S.card, border: `1px solid ${settings.liveTradingEnabled ? "rgba(239,71,111,0.25)" : "rgba(255,255,255,0.04)"}`, display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14 }}>
                <div>
                  <div style={{ fontSize: 11, color: settings.liveTradingEnabled ? C.red : "rgba(255,255,255,0.6)" }}>Live Trading</div>
                  <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>Allow real Coinbase orders — real money at risk</div>
                </div>
                <div onClick={() => handleSettingsChange('liveTradingEnabled', !settings.liveTradingEnabled)} style={{ width: 44, height: 24, borderRadius: 12, cursor: "pointer", background: settings.liveTradingEnabled ? "rgba(239,71,111,0.4)" : "rgba(255,255,255,0.1)", border: `1px solid ${settings.liveTradingEnabled ? "rgba(239,71,111,0.6)" : "rgba(255,255,255,0.15)"}`, position: "relative", transition: "all 0.3s" }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: settings.liveTradingEnabled ? C.red : "rgba(255,255,255,0.3)", position: "absolute", top: 2, left: settings.liveTradingEnabled ? 22 : 2, transition: "all 0.3s" }} />
                </div>
              </div>
            )}

            <button onClick={() => {
              if (!window.confirm("Reset account to $10,000 and clear all trades?")) return;
              wsSend({ type: 'reset' });
            }} style={{ width: "100%", marginTop: 16, padding: 12, background: "rgba(239,71,111,0.1)", color: C.red, border: "1px solid rgba(239,71,111,0.2)", borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 1.5, fontFamily: "inherit" }}>
              RESET ACCOUNT
            </button>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      {/* Buy amount modal */}
      {buyModal && (() => {
        const BUY_FEE_PCT  = 0.006;
        const amt          = parseFloat(buyAmount);
        const validAmt     = amt > 0 && Number.isFinite(amt);
        const estFee       = validAmt ? amt * BUY_FEE_PCT : null;
        const estTokens    = validAmt && buyModal.price > 0 ? (amt - (estFee ?? 0)) / buyModal.price : null;
        const ticker       = buyModal.symbol?.replace(/-?USD$/, "");
        const priceStr     = buyModal.price < 0.01 ? buyModal.price.toFixed(6) : buyModal.price.toFixed(4);
        const tokDec       = buyModal.price < 0.01 ? 2 : buyModal.price < 1 ? 4 : 6;
        const presets      = [25, 50, 100].filter(p => p <= displayCash);
        return (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div style={{ background: "#0f1520", border: "1px solid rgba(0,245,212,0.25)", borderRadius: 12, padding: 24, width: "100%", maxWidth: 340, fontFamily: "inherit" }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: 2, marginBottom: 4 }}>MANUAL BUY</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 14 }}>{buyModal.symbol}</div>

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 4 }}>
                <span>Current price</span><span style={{ color: "#fff", fontWeight: 600 }}>${priceStr}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "rgba(255,255,255,0.45)", marginBottom: 16 }}>
                <span>Available balance</span><span style={{ color: C.teal, fontWeight: 600 }}>${displayCash.toFixed(2)}</span>
              </div>

              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", letterSpacing: 1.5, marginBottom: 8 }}>AMOUNT TO SPEND</div>
              {presets.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  {presets.map(p => (
                    <button key={p} onClick={() => setBuyAmount(String(p))} style={{ flex: 1, padding: "7px 0", background: buyAmount === String(p) ? "rgba(0,245,212,0.15)" : "rgba(255,255,255,0.04)", color: buyAmount === String(p) ? C.teal : "rgba(255,255,255,0.5)", border: `1px solid ${buyAmount === String(p) ? "rgba(0,245,212,0.4)" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}>${p}</button>
                  ))}
                  <button onClick={() => setBuyAmount('')} style={{ flex: 1, padding: "7px 0", background: buyAmount !== '' && !presets.map(String).includes(buyAmount) ? "rgba(0,245,212,0.15)" : "rgba(255,255,255,0.04)", color: buyAmount !== '' && !presets.map(String).includes(buyAmount) ? C.teal : "rgba(255,255,255,0.5)", border: `1px solid ${buyAmount !== '' && !presets.map(String).includes(buyAmount) ? "rgba(0,245,212,0.4)" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}>CUSTOM</button>
                </div>
              )}
              <input
                autoFocus
                type="number"
                min="1"
                step="1"
                placeholder={`Enter amount (max $${displayCash.toFixed(2)})`}
                value={buyAmount}
                onChange={e => setBuyAmount(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmBuy(); if (e.key === 'Escape') setBuyModal(null); }}
                style={{ width: "100%", padding: "10px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(0,245,212,0.3)", borderRadius: 6, color: "#fff", fontSize: 16, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 12 }}
              />

              {validAmt && (
                <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 6, padding: "10px 12px", marginBottom: 14, fontSize: 11 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "rgba(255,255,255,0.45)", marginBottom: 4 }}>
                    <span>Estimated {ticker} received</span>
                    <span style={{ color: "#fff", fontWeight: 600 }}>{estTokens != null ? estTokens.toFixed(tokDec) : "—"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", color: "rgba(255,255,255,0.45)" }}>
                    <span>Estimated fee (~0.6%)</span>
                    <span style={{ color: C.yellow, fontWeight: 600 }}>${estFee != null ? estFee.toFixed(2) : "—"}</span>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setBuyModal(null)} style={{ flex: 1, padding: 10, background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.4)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: 1 }}>CANCEL</button>
                <button onClick={confirmBuy} disabled={!validAmt || amt > displayCash} style={{ flex: 2, padding: 10, background: validAmt && amt <= displayCash ? "linear-gradient(135deg, rgba(0,245,212,0.25), rgba(122,229,130,0.2))" : "rgba(255,255,255,0.04)", color: validAmt && amt <= displayCash ? C.teal : "rgba(255,255,255,0.2)", border: `1px solid ${validAmt && amt <= displayCash ? "rgba(0,245,212,0.4)" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: validAmt && amt <= displayCash ? "pointer" : "default", fontFamily: "inherit", letterSpacing: 1 }}>
                  {validAmt ? `BUY $${amt.toFixed(2)} OF ${buyModal.symbol}` : "CONFIRM BUY"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, background: "linear-gradient(to top, #0a0e17 80%, transparent)", paddingTop: 20, zIndex: 9999 }}>
        <div style={{ display: "flex", justifyContent: "space-around", padding: "10px 8px 16px", background: "rgba(10,14,23,0.95)", borderTop: "1px solid rgba(255,255,255,0.06)", backdropFilter: "blur(20px)" }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, color: tab === t ? C.teal : "rgba(255,255,255,0.25)", transition: "color 0.2s", fontFamily: "inherit", padding: "4px 8px" }}>
              <span style={{ fontSize: 18 }}>{TAB_ICONS[t]}</span>
              <span style={{ fontSize: 8, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase" }}>{t}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
